import styled, { keyframes, css } from 'styled-components';
import tw from 'twin.macro';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import 'react-calendar/dist/Calendar.css';
import { ProtectionPlanEnum, ProductType } from '../../../../__generated__/graphql';
import { useQuery, useLazyQuery, useMutation, ApolloError } from '@apollo/client';
import {
  CAR_NOT_BOOKABLE,
  graphqlErrorCode,
  graphqlErrorMessage,
} from '../../../utils/listingHold';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getBrandSiteHost, getCurrentSiteHost } from '../../../utils/siteHost';
import { getReferralMarkerForBooking } from '../../../utils/referralAttribution';
import {
  buildOwnerCheckoutRedirectUrl,
  decideBrandRedirect,
  normalizeReferralSiteMarker,
} from '../../../utils/referralRedirectUrl';
import {
  clearReferralHandoffFromUrl,
  formatOwnerSiteDisplayHost,
  isReferralHandoffLanding,
} from '../../../utils/referralHandoff';
import { getCrossSiteSsoExchangePromise } from '../../../utils/crossSiteSso';
import {
  AirportNotBookableNotice,
  OwnerSiteRedirectNotice,
  ReferralOwnerTermsModal,
} from '../../../components/referral/ReferralCheckoutModals';
import {
  CALCULATE_CAR_PRICING_WITHOUT_TAX,
  GET_CAR,
  GET_CAR_CHECKOUT_BRAND_SITE,
} from '../../../services/carService/queries';
import { carAsRented } from '../../../utils/carTripSettings';
import { MONTH_DAYS, daysBetween, parseTripModeParams } from '../../../helpers/monthlyTrip';
import type { TripPick } from '../../../components/bookCard/DateRangeModal';
import { useBrandLocale } from '../../../hooks/useBrandLocale';
import { driverAgeOptions } from '../../../utils/brandAge';
import { useBrandRules } from '../../../hooks/useHostCapabilities';
import { formatAddress, sanitizeAddressString } from '../../../helpers/formatAddress';
import { toast } from 'react-toastify';
import { IGetCarQuery } from '../../../services/carService/__generated__/queries.generated';
import { useGetCarBlackoutDaysHook } from '../../../hooks/useGetCarBlackoutDaysHook';
import { GET_USER } from '../../../services/authService/queries';
import { CREATE_CROSS_SITE_SSO_CODE } from '../../../services/authService/queries';
import queryString from 'query-string';
import { useSnapshot } from 'valtio';
import { authStore } from '../../../../store/authStore';
import { DateRangeModal } from '../../../components/bookCard/DateRangeModal';
import {
  computeBlockedDateSets,
  todayKey as listingTodayKey,
  validateRentalDateRange,
} from '../../../helpers/bookedSlots';
import {
  GET_ALLOWED_LOCATIONS,
  GET_LOCATIONS,
  GET_LOCATION,
} from '../../../services/locationServices/queries';
import {
  IGetAllowedLocationsQuery,
  IGetAllowedLocationsQueryVariables,
  IGetLocationsQuery,
  IGetLocationsQueryVariables,
  IGetLocationQuery,
  IGetLocationQueryVariables,
} from '../../../services/locationServices/__generated__/queries.generated';
import { Button } from '../../../components/button';
import { URLS } from '../../../../constants/urls';
import { useCreateBookingIntentMutation } from '../../../services/bookingService/__generated__/queries.generated';
import { useBookingDateTime } from '../../../hooks/useBookingDateTime';
import { ViewCarPageSkeleton } from '../../../components/skeletons';
import { formatNumber } from '../../../helpers/formatNumber';
import { CircleLoader, MoonLoader } from 'react-spinners';
import { ErrorCode } from '../../../../__generated__/graphql';
import { modalActions } from '../../../../store/authStore/modalStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faUser,
  faGasPump,
  faCog,
  faCamera,
  faHeart as faHeartSolid,
  faShieldAlt,
  faCreditCard,
  faCheckCircle,
  faClock,
  faTachometerAlt,
  faClose,
} from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartOutline } from '@fortawesome/free-regular-svg-icons';
import { SedanIcon } from '../../../components/vehicleIcons';
import { SCREENS, SCREENS_MAX } from '../../../components/responsive';
import { combinePickerDateTimeGetUTC } from '../../../helpers/combinePickerDateTimeGetUTC';
import { getTodayInTimezone } from '../../../helpers/timezoneHelpers';
import { DateTime } from 'luxon';
import { pickHomeOperatingHours } from '../../../helpers/operatingHours';
import { useFavorites } from '../../../hooks/useFavorites';
import { Colors } from '../../../styles/colors';
import { CarRatingsSection } from '../../../components/ratings';
import { TripLocation } from '../../../components/car/TripLocation';

const PageContainer = styled.div`
  ${tw`box-border w-full max-w-full min-w-0 max-w-screen-xl mx-auto px-4 md:px-6 lg:px-0 pt-4 md:pt-6 pb-8 md:pb-16 overflow-x-hidden`}
`;

// Mobile-only wrapper - shows on small screens, hidden on md+
const MobileOnly = styled.div`
  ${tw`block w-full max-w-full min-w-0 box-border`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`hidden`}
  }
`;

// Desktop-only wrapper - hidden on small screens, shows on md+
const DesktopOnly = styled.div`
  ${tw`hidden`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`block`}
  }
`;

// Main layout container - two columns on md+, single column on mobile
const FlexContainer = styled.div`
  ${tw`flex flex-col gap-6`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`flex-row`}
  }
`;

// Left column - vehicle details (2/3 width on md+)
const DetailsSection = styled.div`
  ${tw`w-full`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`w-2/3`}
  }
`;

// Right column - booking section (1/3 width on md+)
const BookingSection = styled.div`
  ${tw`w-full`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`w-1/3`}
    position: sticky;
    top: 20px;
    height: fit-content;
  }
  ${tw`p-4 border rounded-lg shadow-card bg-white`}
`;

// Image gallery styles
const ImageGalleryContainer = styled.div`
  ${tw`mb-6`}
`;

// Desktop-only ImageGalleryContainer
const DesktopImageGalleryContainer = styled(ImageGalleryContainer)`
  ${tw`hidden`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`block`}
  }
`;

// Mobile thumbnail wrapper
const MobileThumbnailWrapper = styled.div`
  ${tw`mb-4 block`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`hidden`}
  }
`;

const MobileThumbnailContainer = styled.div`
  ${tw`relative w-full rounded-lg overflow-hidden`}
`;

const MobileThumbnailImage = styled.img`
  ${tw`w-full h-auto block`}
`;

// Mobile title and rating wrapper
const MobileTitleRatingWrapper = styled.div`
  ${tw`mb-4 block`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`hidden`}
  }
`;

// Mobile calendar wrapper
const MobileCalendarWrapper = styled.div`
  ${tw`mb-6 block`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`hidden`}
  }
`;

// Desktop-only wrapper for title and rating
const DesktopTitleRatingWrapper = styled.div`
  ${tw`hidden`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`block`}
  }
`;

const MainImageContainer = styled.div`
  ${tw`relative w-full rounded-lg overflow-hidden mb-4`}
  height: 400px;
  @media (min-width: ${SCREENS.md}) {
    height: 500px;
  }
`;

const MainImage = styled.img`
  ${tw`w-full h-full object-cover`}
`;

const ThumbnailContainer = styled.div`
  ${tw`grid grid-cols-2 gap-2`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`grid-cols-3`}
  }
`;

const ThumbnailImage = styled.div`
  ${tw`relative rounded-lg overflow-hidden cursor-pointer`}
  height: 120px;
  @media (min-width: ${SCREENS.md}) {
    height: 150px;
  }
`;

const ThumbnailImg = styled.img`
  ${tw`w-full h-full object-cover`}
`;

const ViewPhotosButton = styled.button`
  ${tw`absolute bottom-2 right-2 bg-black bg-opacity-70 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm font-medium`}
  &:hover {
    ${tw`bg-opacity-90`}
  }
`;

const FavoriteButton = styled.button<{ $isFavorite?: boolean }>`
  ${tw`absolute top-4 right-4 bg-white border-none flex items-center justify-center cursor-pointer`}
  padding: 7px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  svg {
    color: ${props => (props.$isFavorite ? '#E11D48' : 'var(--color-text-secondary)')};
    font-size: 18px;
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

// Vehicle title and specs
const CarTitle = styled.h1`
  ${tw`text-3xl font-bold mb-2`}
`;

const ModelType = styled.div`
  ${tw`text-text-secondary text-lg mb-4`}
`;

const RatingSection = styled.div`
  ${tw`flex items-center gap-2 mb-4`}
`;

const RatingText = styled.span`
  ${tw`font-semibold`}
`;

const TripCount = styled.span`
  ${tw`text-text-secondary`}
`;

const CarSpecs = styled.div`
  ${tw`flex flex-wrap gap-6 mb-6`}
`;

const SpecItem = styled.div`
  ${tw`flex items-center gap-2`}
`;

// Host section
const HostSection = styled.div`
  ${tw`mt-8 pt-8 border-t border-border-default`}
`;

const SectionLabel = styled.div`
  ${tw`text-sm font-semibold uppercase mb-4 text-text-secondary`}
`;

const HostInfo = styled.div`
  ${tw`flex items-center gap-4`}
`;

const HostImage = styled.img`
  ${tw`w-14 h-14 rounded-full object-cover`}
`;

const HostDetails = styled.div`
  ${tw`flex flex-col`}
`;

const HostName = styled.div`
  ${tw`font-semibold text-lg`}
`;

const HostStats = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const HostRating = styled.div`
  ${tw`flex items-center gap-1 text-sm`}
`;

const HostRatingBadge = styled.span`
  ${tw`px-2 py-0.5 rounded-md text-xs font-semibold`}
  background-color: ${Colors.Gray100};
  color: ${Colors.Black};
`;

// Features section
const FeaturesSection = styled.div`
  ${tw`mt-8 pt-8 border-t border-border-default`}
`;

const FeaturesGrid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 gap-4`}
`;

const FeatureCategory = styled.div`
  ${tw`mb-4`}
`;

const CategoryTitle = styled.div`
  ${tw`font-semibold mb-2`}
`;

const FeatureList = styled.div`
  ${tw`flex flex-col gap-2`}
`;

const FeatureItem = styled.div`
  ${tw`flex items-center gap-2 text-text-secondary`}
`;

// Included in price section
const IncludedSection = styled.div`
  ${tw`mt-8 pt-8 border-t border-border-default`}
`;

const IncludedCategory = styled.div`
  ${tw`mb-6`}
`;

const IncludedItem = styled.div`
  ${tw`flex items-start gap-3 mb-4`}
`;

const IncludedIcon = styled.div`
  ${tw`text-text-secondary flex-shrink-0 mt-0.5`}
`;

const IncludedContent = styled.div`
  ${tw`flex-1`}
`;

const IncludedTitle = styled.div`
  ${tw`font-medium mb-1`}
`;

const IncludedDescription = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

// Description
const Description = styled.div`
  ${tw`mt-8 pt-8 border-t border-border-default`}
`;

const DescriptionText = styled.p`
  ${tw`text-text-secondary whitespace-pre-line leading-relaxed`}
`;

// Booking section styles
const BookingHeader = styled.div`
  ${tw`mb-6`}
`;

const PriceSection = styled.div`
  ${tw`mb-6`}
`;

const PriceAmount = styled.div`
  ${tw`text-3xl font-bold mb-1`}
`;

const PriceOriginal = styled.span`
  ${tw`line-through text-text-secondary text-xl mr-2`}
`;

const PriceBeforeTaxes = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

// Desktop-only PriceSection
const DesktopPriceSection = styled(PriceSection)`
  ${tw`hidden`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`block`}
  }
`;

const TripSection = styled.div`
  ${tw`mb-6`}
`;

// Desktop-only TripSection
const DesktopTripSection = styled(TripSection)`
  ${tw`hidden`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`block`}
  }
`;

const TripLabel = styled.div`
  ${tw`text-sm font-semibold mb-3`}
`;

// Mobile fixed price bar at bottom
const MobileFixedPriceBar = styled.div`
  @media (max-width: ${SCREENS_MAX.md}) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${Colors.White};
    border-top: 1px solid ${Colors.Gray200};
    padding: 16px;
    box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
    z-index: 50;
  }
  @media (min-width: ${SCREENS.md}) {
    display: none;
  }
`;

const MobilePriceContent = styled.div`
  ${tw`flex items-center justify-between`}
`;

const MobilePriceAmount = styled.div`
  ${tw`text-2xl font-bold`}
`;

const MobileContinueButton = styled.button`
  ${tw`px-6 py-3 text-white font-semibold rounded-lg transition-colors`}
  background-color: ${Colors.Black};
  &:hover {
    background-color: ${Colors.BlackHover};
  }
`;

const MobilePriceBeforeTaxes = styled.div`
  ${tw`text-xs text-text-secondary`}
`;

// Photo Gallery Modal Styles
const PhotoModalOverlay = styled.div`
  ${tw`fixed inset-0 z-50 bg-white`}
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const PhotoModalContent = styled.div`
  ${tw`relative w-full min-h-full flex flex-col bg-white`}
  max-width: 1280px;
  margin: 0 auto;
`;

const PhotoModalHeader = styled.div`
  ${tw`sticky top-0 flex items-center p-4 bg-white z-10 justify-end`}
  border-bottom: 1px solid var(--color-border);
`;

const PhotoModalCloseButton = styled.button`
  ${tw`text-2xl cursor-pointer hover:opacity-70 z-10 border-none bg-transparent`}
`;

const PhotoModalBody = styled.div`
  ${tw`flex-1 p-4`}
`;

// Photo Grid - 2 columns on desktop, 1 column on mobile
const PhotoGrid = styled.div`
  ${tw`grid gap-2`}
  grid-template-columns: 1fr;
  @media (min-width: ${SCREENS.md}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const PhotoGridItem = styled.div`
  ${tw`relative w-full overflow-hidden`}
  @media (min-width: ${SCREENS.md}) {
    border-radius: 8px;
  }
`;

const PhotoGridImg = styled.img`
  ${tw`w-full`}
  display: block;
  height: auto;
`;

// Mobile stacked images (before modal opens)
const MobileStackedImages = styled.div`
  ${tw`flex flex-col gap-2`}
`;

const MobileStackedImageItem = styled.div`
  ${tw`relative w-full rounded-lg overflow-hidden`}
  aspect-ratio: 4/3;
`;

const MobileStackedImage = styled.img`
  ${tw`w-full h-full object-cover`}
`;

// Add bottom padding to page container on mobile to account for fixed price bar
const MobilePagePadding = styled.div`
  @media (max-width: ${SCREENS_MAX.md}) {
    padding-bottom: 100px;
  }
`;

const TripSavingsSection = styled.div`
  ${tw`mb-6 p-4 bg-bg-secondary rounded-lg`}
`;

const SavingsItem = styled.div`
  ${tw`flex justify-between items-center`}
`;

const SavingsLabel = styled.div`
  ${tw`text-text-secondary`}
`;

const SavingsAmount = styled.div`
  ${tw`text-green-600 font-semibold`}
`;

const ContinueButton = styled.button`
  ${tw`w-full py-3 px-6 text-white font-semibold rounded-lg transition-colors mb-3`}
  background-color: ${Colors.Black};
  &:hover {
    background-color: ${Colors.BlackHover};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PolicySection = styled.div`
  ${tw`mb-6`}
`;

const PolicyItem = styled.div`
  ${tw`flex items-start gap-3 mb-4`}
`;

const PolicyIcon = styled.div`
  ${tw`flex-shrink-0 mt-0.5`}
  color: ${Colors.Black};
`;

const PolicyContent = styled.div`
  ${tw`flex-1`}
`;

const PolicyTitle = styled.div`
  ${tw`font-medium mb-1`}
`;

const PolicyDescription = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const PaymentOptionsSection = styled.div`
  ${tw`mb-6`}
`;

const DistanceSection = styled.div``;

const Label = styled.div`
  ${tw`text-sm text-text-secondary mb-1`}
`;

const DatePickerContainer = styled.div`
  ${tw`relative mb-4 w-full`}
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;

const DatePickerWrapper = styled.div<{ $hasError?: boolean; $shouldShake?: boolean }>`
  transition: all 0.3s ease;
  ${({ $hasError }) =>
    $hasError &&
    `
    box-shadow: 0 0 0 2px ${Colors.Error};
    border-radius: 0.5rem;
    padding: 0.25rem;
  `}
  ${({ $shouldShake }) =>
    $shouldShake &&
    css`
      animation: ${shake} 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
    `}
`;

const DateRangeTrigger = styled.button`
  ${tw`
    w-full
    border
    border-border-default
    rounded-lg
    bg-white
    p-3
    flex
    items-center
    justify-between
    h-[44px]
    text-left
  `}
  &:hover {
    ${tw`border-gray-400`}
  }
`;

const DateRangeText = styled.div`
  ${tw`text-sm font-medium text-text-primary`}
`;

const DateRangeSubText = styled.div`
  ${tw`text-xs text-text-secondary`}
`;

const TimeSelectRow = styled.div`
  ${tw`mt-3 grid grid-cols-2 gap-3`}
`;

const TimeSelect = styled.select`
  ${tw`
    w-full
    p-2
    border
    border-border-default
    rounded-lg
    bg-white
    cursor-pointer
    h-[44px]
  `}
  &:hover {
    ${tw`border-gray-400`}
  }
`;

const DateErrorContainer = styled.div<{ $shouldShake?: boolean }>`
  ${tw`mt-3 p-3 rounded-md`}
  background-color: ${Colors.Red50};
  box-shadow: 0 0 0 2px ${Colors.Error};
  border-left: 4px solid ${Colors.Error};
  ${({ $shouldShake }) =>
    $shouldShake &&
    css`
      animation: ${shake} 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
    `}
`;

const DateErrorText = styled.div`
  ${tw`text-sm text-red-700 font-medium`}
`;

const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
const fourDaysFromNow = new Date();
fourDaysFromNow.setDate(fourDaysFromNow.getDate() + 4);

const StyledSelect = styled.select`
  ${tw`
    w-full
    p-2
    border
    border-border-default
    rounded-lg
    bg-white
    cursor-pointer
    h-[44px]
    mb-4
  `}
  &:hover {
    ${tw`border-gray-400`}
  }
`;

const CheckboxContainer = styled.div`
  ${tw`flex items-center gap-2 mb-4`}
`;

const Checkbox = styled.input`
  ${tw`cursor-pointer`}
  ${({ disabled }: { disabled?: boolean }) =>
    disabled &&
    tw`
      hidden
    `}
`;

const CheckboxLabel = styled.label<{ disabled: boolean }>`
  ${tw`text-sm text-text-secondary cursor-pointer`}
  ${({ disabled }) =>
    disabled &&
    tw`
      opacity-50
      cursor-not-allowed
    `}
`;

const PartialDayTile = styled.div<{ isPartiallyBooked: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;

  ${({ isPartiallyBooked }) =>
    isPartiallyBooked &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 50%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.1);
      pointer-events: none;
    }
  `}
`;

const SubmitContainer = styled.div`
  ${tw`flex flex-row gap-4 mt-4`}
`;

const AgeSelectContainer = styled.div`
  ${tw`mb-4`}
`;

// Desktop-only AgeSelectContainer
const DesktopAgeSelectContainer = styled(AgeSelectContainer)`
  ${tw`hidden`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`block`}
  }
`;

const StyledAgeSelect = styled.select`
  ${tw`h-[44px] p-2 border border-border-default rounded-lg bg-white cursor-pointer w-full`}
  &:hover {
    ${tw`border-gray-400`}
  }
`;

// Desktop-only Continue Button wrapper
const DesktopContinueButtonWrapper = styled.div`
  ${tw`hidden`}
  @media (min-width: ${SCREENS.md}) {
    ${tw`block`}
  }
`;

const GuestViewCarPage = () => {
  const { search } = useLocation();
  const {
    locationId: urlLocationId,
    returnDate: urlReturnDate,
    returnLocationId: urlReturnLocationId,
    returnTime: urlReturnTime,
    startTime: urlStartTime,
    startDate: urlStartDate,
    driverAge: urlDriverAge,
    mode: urlTripMode,
    months: urlMonths,
  } = queryString.parse(search, {
    parseNumbers: true,
    parseBooleans: true,
  });
  // Spec 028: the trip picker opens in Months mode after a monthly search.
  const urlTripPick = parseTripModeParams({ mode: urlTripMode, months: urlMonths });
  const { id: carId } = useParams<{ id: string }>();
  const {
    data: locationsData,
    loading: locationsLoading,
    error: locationsError,
  } = useQuery<IGetLocationsQuery, IGetLocationsQueryVariables>(GET_LOCATIONS, {
    fetchPolicy: 'cache-first',
  });
  const {
    data: storedCarData,
    loading: carLoading,
    error: carError,
  } = useQuery<IGetCarQuery>(GET_CAR, {
    variables: { id: carId },
  });
  // Spec 028: tripSettings gives the allowance in this site's unit.
  const { distanceUnit } = useBrandLocale();
  // Spec 014b: the car as this site's brand rents it (deposit, miles, trip length, age).
  const carData = useMemo(
    () =>
      storedCarData ? { ...storedCarData, car: carAsRented(storedCarData.car) } : storedCarData,
    [storedCarData],
  );

  const [carLocations, setCarLocations] = useState<any[] | undefined>([]);

  const [createBookingIntent, { loading: makeBookingLoading }] = useCreateBookingIntentMutation();
  const [createCrossSiteSsoCode] = useMutation(CREATE_CROSS_SITE_SSO_CODE);

  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useQuery<any>(GET_USER, {
    variables: { id: carData?.car?.owner?.id },
    skip: !carData?.car?.owner?.id,
  });

  const {
    blackoutDayKeys,
    bookedSlots,
    tripBufferHours,
    timezone: carTimezone,
    loading: loadingBlackoutDays,
    error: errorBlackoutDays,
  } = useGetCarBlackoutDaysHook({
    carId,
    excludeBookingId: null,
    priceCalendar: carData?.car?.priceCalendar,
    timezone: carData?.car?.location?.timezone,
  });

  const homeOperatingHours = useMemo(
    () => pickHomeOperatingHours(carData?.car?.location?.operatingHours ?? undefined),
    [carData?.car?.location?.operatingHours],
  );

  const { user } = useSnapshot(authStore);
  const auth = useSnapshot(authStore);
  const navigate = useNavigate();

  const {
    startDate,
    endDate,
    startTime,
    endTime,
    startTimestamp,
    endTimestamp,
    startAtOptions,
    endAtOptions,
    validationError,
    setStartDate,
    setEndDate,
    setStartTime,
    setEndTime,
  } = useBookingDateTime({
    timezone: carTimezone ?? carData?.car?.location?.timezone ?? undefined,
    bookedSlots,
    bufferHours: tripBufferHours,
    initialStartDate: urlStartDate ? new Date(String(urlStartDate)) : undefined,
    initialEndDate: urlReturnDate ? new Date(String(urlReturnDate)) : undefined,
    initialStartTime: urlStartTime ? String(urlStartTime) : undefined,
    initialEndTime: urlReturnTime ? String(urlReturnTime) : undefined,
    minimumRentalDays: carData?.car?.minimumRentalDays || 1,
    operatingHours: homeOperatingHours,
  });

  // Spec 028: Months mode only where the brand and this car rent by the month. It opens in
  // Months after a monthly search, or when the dates already span 30 days or more.
  const { effectiveMinimumAge, monthlyRentalOffered } = useBrandRules();
  const monthlyAllowed = monthlyRentalOffered && carData?.car?.monthlyRentalIsOn === true;
  const [tripPick, setTripPick] = useState<TripPick>(() => ({
    mode:
      urlTripPick.mode === 'months' ||
      daysBetween(
        DateTime.fromJSDate(startDate).toISODate() ?? '',
        DateTime.fromJSDate(endDate).toISODate() ?? '',
      ) >= MONTH_DAYS
        ? 'months'
        : 'dates',
    months: urlTripPick.months,
  }));

  const minSelectableDate =
    carTimezone || carData?.car?.location?.timezone
      ? getTodayInTimezone(carTimezone || carData?.car?.location?.timezone || 'America/Los_Angeles')
      : new Date();

  // Flutter parity: days fully covered by a booking (plus the trip buffer) or closed by
  // operating hours cannot be picked; partly covered days stay pickable with a marker.
  const listingZone = carTimezone || carData?.car?.location?.timezone || 'America/Los_Angeles';
  const tripDaySets = useMemo(() => {
    const fromKey = listingTodayKey(listingZone);
    const toKey = DateTime.fromISO(fromKey).plus({ years: 1 }).toISODate() || fromKey;
    const sets = computeBlockedDateSets({
      slots: bookedSlots,
      bufferHours: tripBufferHours,
      fromKey,
      toKey,
      timezone: listingZone,
      operatingHours: homeOperatingHours,
    });
    blackoutDayKeys.forEach(key => {
      sets.blocked.add(key);
      sets.partial.delete(key);
    });
    return sets;
  }, [bookedSlots, tripBufferHours, listingZone, homeOperatingHours, blackoutDayKeys]);

  const validateTripRange = (startKey: string, endKey: string): string | null => {
    const startMs = combinePickerDateTimeGetUTC(
      new Date(`${startKey}T00:00:00`),
      startTime,
      listingZone,
    );
    let endMs = combinePickerDateTimeGetUTC(new Date(`${endKey}T00:00:00`), endTime, listingZone);
    // Same-day pair with the return at or before the pickup: nudge it one step later first.
    if (endMs <= startMs) endMs = startMs + 30 * 60 * 1000;
    return validateRentalDateRange({
      startMs,
      endMs,
      slots: bookedSlots,
      bufferHours: tripBufferHours,
      timezone: listingZone,
      operatingHours: homeOperatingHours,
      blocked: tripDaySets.blocked,
    });
  };

  const [pickupLocationId, setPickupLocationId] = useState<number | null>(
    Number(urlLocationId) || null,
  );
  const [returnLocationId, setReturnLocationId] = useState<number | null>(
    Number(urlReturnLocationId) || null,
  );
  const [pickupLocationData, setPickupLocationData] = useState<
    IGetLocationQuery['location'] | null
  >(null);
  const [returnLocationData, setReturnLocationData] = useState<
    IGetLocationQuery['location'] | null
  >(null);

  const [driverAge, setDriverAge] = useState<number>(Number(urlDriverAge) || 25);
  // Spec 014b US-5: no age range below this site's minimum driver age.
  const ageOptions = useMemo(() => driverAgeOptions(effectiveMinimumAge), [effectiveMinimumAge]);
  useEffect(() => {
    if (driverAge < effectiveMinimumAge) setDriverAge(effectiveMinimumAge);
  }, [driverAge, effectiveMinimumAge]);

  const [protectionPlan, setProtectionPlan] = useState<string>(ProtectionPlanEnum.Standard);

  const [allowedLocations, setAllowedLocations] = useState<
    IGetAllowedLocationsQuery['getAllowedLocations'] | null
  >(null);

  const [differentReturnLocation, setDifferentReturnLocation] = useState(
    urlLocationId !== urlReturnLocationId || false,
  );

  const [dateConflictError, setDateConflictError] = useState<string | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);

  const [shouldShake, setShouldShake] = useState(false);
  const [showOwnerTermsModal, setShowOwnerTermsModal] = useState(false);
  const [redirectInProgress, setRedirectInProgress] = useState(false);
  const [handoffCheckoutInProgress, setHandoffCheckoutInProgress] = useState(false);
  const handoffTermsPromptedRef = useRef(false);
  const prefetchedSsoCodeRef = useRef<string | undefined>(undefined);
  const dateErrorRef = useRef<HTMLDivElement>(null);
  const mobileDateErrorRef = useRef<HTMLDivElement>(null);
  const lastErrorKeyRef = useRef<string | null>(null);
  const [isDateModalOpen, setDateModalOpen] = useState(false);

  const triggerErrorEffects = useCallback(() => {
    setShouldShake(true);
    setTimeout(() => setShouldShake(false), 500);

    const refToScroll = window.innerWidth < 768 ? mobileDateErrorRef : dateErrorRef;

    if (refToScroll.current) {
      setTimeout(() => {
        refToScroll.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, []);

  const formatDateLabel = (date: Date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatDateRangeLabel = (start: Date, end: Date) => {
    if (!start || !end) return 'Select dates';
    return `${formatDateLabel(start)} - ${formatDateLabel(end)}`;
  };

  const handlePriceError = useCallback((errorMessage: string) => {
    setPricingError(errorMessage);
  }, []);

  useEffect(() => {
    if (!startDate || !endDate || !startTime || !endTime) return;

    const currentParams = queryString.parse(search);
    const nextQuery = {
      ...currentParams,
      startDate: startDate.toISOString(),
      returnDate: endDate.toISOString(),
      startTime,
      returnTime: endTime,
      driverAge: driverAge.toString(),
      timezone: carTimezone ?? carData?.car?.location?.timezone ?? currentParams.timezone,
    };
    const nextSearch = `?${queryString.stringify(nextQuery, {
      skipNull: true,
      skipEmptyString: true,
    })}`;

    if (nextSearch !== search) {
      navigate({ search: nextSearch }, { replace: true });
    }
  }, [
    startDate,
    endDate,
    startTime,
    endTime,
    driverAge,
    carTimezone,
    carData?.car?.location?.timezone,
    search,
    navigate,
  ]);

  useEffect(() => {
    setPricingError(null);
  }, [startTimestamp, endTimestamp, driverAge]);

  const [getAllowedLocations, { data: allowedLocationsData, loading: getAllowedLocationsLoading }] =
    useLazyQuery<IGetAllowedLocationsQuery, IGetAllowedLocationsQueryVariables>(
      GET_ALLOWED_LOCATIONS,
    );

  // Query for pickup location if it's not in locationsData (e.g., airport/public location)
  const [getPickupLocation, { data: pickupLocationQueryData }] = useLazyQuery<
    IGetLocationQuery,
    IGetLocationQueryVariables
  >(GET_LOCATION);

  // Query for return location if it's different from pickup and not in locationsData
  const [getReturnLocation, { data: returnLocationQueryData }] = useLazyQuery<
    IGetLocationQuery,
    IGetLocationQueryVariables
  >(GET_LOCATION);

  // Handle carData changes - set pickup and return location IDs
  useEffect(() => {
    if (carData?.car) {
      setPickupLocationId(Number(urlLocationId) || carData.car.location?.id || null);
      setReturnLocationId(Number(urlReturnLocationId) || carData.car.location?.id || null);
    }
  }, [carData, urlLocationId, urlReturnLocationId]);

  // Handle carError - show error toast
  useEffect(() => {
    if (carError) {
      toast.error('loading car data failed' + carError?.message);
    }
  }, [carError]);

  // Handle userData changes - set carLocations
  useEffect(() => {
    if (userData?.user?.locations) {
      setCarLocations(userData.user.locations);
    }
  }, [userData]);

  // Handle allowedLocationsData changes
  useEffect(() => {
    if (allowedLocationsData?.getAllowedLocations) {
      const filteredLocations = allowedLocationsData.getAllowedLocations.filter(
        location => location.isActive,
      );
      setAllowedLocations(filteredLocations.length === 0 ? [] : filteredLocations);
    }
  }, [allowedLocationsData]);

  // Handle pickupLocationQueryData changes
  useEffect(() => {
    if (pickupLocationQueryData?.location) {
      setPickupLocationData(pickupLocationQueryData.location);
    }
  }, [pickupLocationQueryData]);

  // Handle returnLocationQueryData changes
  useEffect(() => {
    if (returnLocationQueryData?.location) {
      setReturnLocationData(returnLocationQueryData.location);
    }
  }, [returnLocationQueryData]);

  useEffect(() => {
    if (pickupLocationId || pickupLocationId === 0) {
      getAllowedLocations({
        variables: {
          locationId: pickupLocationId,
        },
      });
    }
  }, [pickupLocationId, getAllowedLocations]);

  // Fetch pickup location if it's not in locationsData
  useEffect(() => {
    if (pickupLocationId && locationsData?.locations) {
      const locationInData = locationsData.locations.find(loc => loc.id === pickupLocationId);
      if (!locationInData) {
        // Location not in user's locations, likely a public location (airport)
        getPickupLocation({ variables: { id: pickupLocationId } });
      } else {
        setPickupLocationData(null);
      }
    }
  }, [pickupLocationId, locationsData, getPickupLocation]);

  // Fetch return location if it's different from pickup and not in locationsData
  useEffect(() => {
    if (returnLocationId && returnLocationId !== pickupLocationId && locationsData?.locations) {
      const locationInData = locationsData.locations.find(loc => loc.id === returnLocationId);
      if (!locationInData) {
        // Location not in user's locations, likely a public location (airport)
        getReturnLocation({ variables: { id: returnLocationId } });
      } else {
        setReturnLocationData(null);
      }
    }
  }, [returnLocationId, pickupLocationId, locationsData, getReturnLocation]);

  useEffect(() => {
    if (!getAllowedLocationsLoading && allowedLocations?.length === 0 && differentReturnLocation) {
      setDifferentReturnLocation(false);
    }
  }, [allowedLocations, differentReturnLocation, getAllowedLocationsLoading]);

  /** Flutter `validateRentalDateRange` over the chosen instants; sets the inline error. */
  const conflictMessageFor = (): string | null => {
    if (!startTimestamp || !endTimestamp) return null;
    if (bookedSlots.length === 0) return null;
    return validateRentalDateRange({
      startMs: startTimestamp,
      endMs: endTimestamp,
      slots: bookedSlots,
      bufferHours: tripBufferHours,
      timezone: listingZone,
      blocked: tripDaySets.blocked,
    });
  };

  useEffect(() => {
    setDateConflictError(conflictMessageFor());
  }, [startTimestamp, endTimestamp, bookedSlots, tripBufferHours, listingZone, tripDaySets]);

  // D40: the server decides where checkout happens for this car and the chosen
  // pickup and return. Asked again whenever either location changes.
  const { data: checkoutBrandData } = useQuery(GET_CAR_CHECKOUT_BRAND_SITE, {
    variables: {
      id: carId,
      pickupLocationId: pickupLocationId ?? null,
      returnLocationId: returnLocationId ?? pickupLocationId ?? null,
    },
    skip: !carId,
    fetchPolicy: 'network-only',
  });

  const brandRedirectDecision = useMemo(
    () =>
      decideBrandRedirect({
        checkoutBrandSite: checkoutBrandData?.car?.checkoutBrandSite,
        currentSiteHost: getCurrentSiteHost(),
        carOwnerId: carData?.car?.owner?.id,
        currentUserId: user?.id,
      }),
    [checkoutBrandData?.car?.checkoutBrandSite, carData?.car?.owner?.id, user?.id],
  );

  const ownerRedirectTarget = useMemo(() => {
    if (brandRedirectDecision.kind !== 'redirect') {
      return null;
    }
    return {
      ownerSiteUrl: brandRedirectDecision.targetHost,
      ownerSiteHost: brandRedirectDecision.targetBrandSite,
    };
  }, [brandRedirectDecision]);

  // D40: no brand taking part has every chosen airport. Continue is disabled.
  // Spec 015: the same for a car that is unlisted or not recall-checked on this site.
  const carBookability = carData?.car?.bookableOnThisSite;
  const notBookableError =
    carBookability && !carBookability.bookable
      ? carBookability.message || 'This car cannot be booked right now.'
      : brandRedirectDecision.kind === 'airportNotBookable'
        ? "This pickup and return pair can't be booked on any site. Change the pickup or return location to keep this car."
        : null;

  useEffect(() => {
    prefetchedSsoCodeRef.current = undefined;
    if (!ownerRedirectTarget || !auth.isAuthenticated) return;

    void createCrossSiteSsoCode({
      variables: {
        input: {
          targetHost: ownerRedirectTarget.ownerSiteUrl,
          sourceHost: getCurrentSiteHost(),
        },
      },
    })
      .then(({ data }) => {
        prefetchedSsoCodeRef.current = data?.createCrossSiteSsoCode?.code;
      })
      .catch(error => {
        console.warn('Failed to prefetch cross-site SSO code.', error);
        prefetchedSsoCodeRef.current = undefined;
      });
  }, [ownerRedirectTarget?.ownerSiteUrl, auth.isAuthenticated]);

  const handleContinueBooking = async (options?: { skipRedirectCheck?: boolean }) => {
    if (!carId) {
      return toast.error('Car ID is required');
    }

    if (notBookableError) {
      toast.error(notBookableError);
      return;
    }

    // Cross-brand redirect must run synchronously on the click — awaiting SSO here
    // loses the browser user-gesture and navigation gets blocked.
    if (!options?.skipRedirectCheck && ownerRedirectTarget) {
      performOwnerSiteRedirect(ownerRedirectTarget.ownerSiteUrl);
      return;
    }

    const conflictMessage = conflictMessageFor();
    const hasConflict = conflictMessage !== null;
    setDateConflictError(conflictMessage);
    const errorMsg = conflictMessage ?? (validationError || pricingError);

    if (hasConflict || validationError || pricingError) {
      toast.error(errorMsg);
      triggerErrorEffects();
      return;
    }

    if (!carData?.car?.owner?.id) {
      return toast.error('Car owner not found');
    }
    if (!carData?.car?.location?.id) {
      return toast.error('Car location not found');
    }
    if (carData?.car?.owner?.id === user?.id) {
      return toast.error(
        `You cannot book your own ${carData?.car?.productTypeDetails?.type?.toLowerCase() || 'item'}`,
      );
    }
    if (pickupLocationId === null) {
      return toast.error('Please select a pickup location');
    }
    if (!startTimestamp || !endTimestamp) {
      return toast.error('Please select a start and end date');
    }

    const referrerMarker = getReferralMarkerForBooking();

    const createBookingInput: Record<string, any> = {
      carId,
      hostId: carData?.car?.owner?.id,
      startAt: startTimestamp,
      endAt: endTimestamp,
      pickupLocationId: pickupLocationId,
      returnLocationId: returnLocationId ?? pickupLocationId,
      protectionPlan: ProtectionPlanEnum.Standard,
      driverAge,
    };
    if (referrerMarker) {
      createBookingInput.referrer = referrerMarker;
    }

    try {
      const result = await createBookingIntent({
        variables: {
          createBookingInput: createBookingInput as any,
        },
      });

      if (result.data?.createBookingIntent) {
        const bookingIntent = result.data.createBookingIntent;
        const bookingInput = {
          carId: bookingIntent.carId,
          startAt: bookingIntent.startAt,
          endAt: bookingIntent.endAt,
          pickupLocationId: bookingIntent.pickupLocationId,
          returnLocationId: bookingIntent.returnLocationId ?? bookingIntent.pickupLocationId,
        };
        // D20: the referrer is cleared only after payment succeeds (CheckoutPage).
        navigate(`/cars/guest/pay/${carId}/${bookingIntent.id}`, {
          state: { createBookingInput: bookingInput },
        });
      }
    } catch (error: any) {
      if (error.graphQLErrors?.[0]?.extensions?.code === ErrorCode.NoTokenProvided) {
        modalActions.openSignInModal(() => handleContinueBooking(options));
      } else if (graphqlErrorCode(error) === CAR_NOT_BOOKABLE) {
        // Spec 015: the car was unlisted after the page loaded.
        toast.error(graphqlErrorMessage(error, 'This car cannot be booked right now.'));
      } else {
        toast.error('Cannot proceed:' + error.message);
      }
    }
  };

  const performOwnerSiteRedirect = (ownerSiteUrl: string) => {
    // D11, D17: the URL carries the source brand, not a random host.
    const referrerMarker = normalizeReferralSiteMarker(getBrandSiteHost()) || getBrandSiteHost();
    setRedirectInProgress(true);
    const redirectUrl = buildOwnerCheckoutRedirectUrl({
      ownerSiteUrl,
      carId: carId!,
      currentSearch: window.location.search,
      referrerMarker,
      ssoCode: prefetchedSsoCodeRef.current,
    });
    window.location.href = redirectUrl;
  };

  const handleOwnerTermsContinue = async () => {
    setHandoffCheckoutInProgress(true);
    try {
      clearReferralHandoffFromUrl();
      setShowOwnerTermsModal(false);
      await getCrossSiteSsoExchangePromise();
      await handleContinueBooking({ skipRedirectCheck: true });
    } finally {
      setHandoffCheckoutInProgress(false);
    }
  };

  // Auto-trigger checkout after OIDC (Google) login redirect
  // When user clicks "Continue" → Sign in with Google → OIDC redirect → comes back here,
  // the pending_checkout flag in sessionStorage indicates we should auto-proceed to checkout.
  // Must wait for pickupLocationId to be set (by the carData useEffect above) before triggering.
  const pendingCheckoutTriggeredRef = useRef(false);
  useEffect(() => {
    const hasPendingCheckout = sessionStorage.getItem('pending_checkout') === 'true';
    if (
      hasPendingCheckout &&
      auth.isAuthenticated &&
      auth.user &&
      carData?.car &&
      !carLoading &&
      pickupLocationId !== null &&
      !pendingCheckoutTriggeredRef.current
    ) {
      pendingCheckoutTriggeredRef.current = true;
      sessionStorage.removeItem('pending_checkout');
      handleContinueBooking();
    }
  }, [auth.isAuthenticated, auth.user, carData?.car, carLoading, pickupLocationId]);

  useEffect(() => {
    if (handoffTermsPromptedRef.current) return;
    if (!isReferralHandoffLanding(search)) return;
    if (carLoading || !carData?.car) return;
    if (pickupLocationId === null) return;
    if (!checkoutBrandData?.car) return;

    // A handoff landing on a brand that still redirects elsewhere is not ours to prompt.
    if (brandRedirectDecision.kind !== 'stay') {
      return;
    }

    handoffTermsPromptedRef.current = true;
    setShowOwnerTermsModal(true);
  }, [
    search,
    carLoading,
    carData?.car,
    pickupLocationId,
    checkoutBrandData?.car,
    brandRedirectDecision,
  ]);

  const car = carData?.car;
  const carType = car?.productTypeDetails?.type as ProductType | undefined;

  // Get favorites
  const { toggleFavorite, favorites } = useFavorites();
  const isFavorite = car ? favorites.some((favCar: any) => favCar.id === car.id) : false;

  // Photo gallery state
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  // Lock body scroll when photo modal is open
  useEffect(() => {
    if (isPhotoModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isPhotoModalOpen]);

  // Get image URLs and photo count
  const imageUrls = useMemo(() => {
    if (!car) return [];
    if (car.images && car.images.length > 0) {
      return car.images.map(
        (img: { imageUrl: string }) => `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${img.imageUrl}`,
      );
    }
    if (car.thumbnailUrl) {
      return [`${URLS.S3_BUCKET.CAR_THUMBNAILS}/${car.thumbnailUrl}`];
    }
    return [];
  }, [car]);

  // Calculate cancellation deadline (same logic as mobile)
  const cancellationDeadline = useMemo(() => {
    if (!startDate || !startTime || !car?.location?.timezone) {
      return null;
    }

    // Spec 014 Phase 3: this site's cancellation rules (24 hours if the API has none).
    const advanceHours = car?.freeCancellationAdvanceHours ?? 24;

    // Calculate deadline: trip start time minus advance hours
    // Calculate deadline: trip start time minus advance hours (convert hours to milliseconds)
    const timezone = car.location.timezone;
    const startTimestamp = combinePickerDateTimeGetUTC(startDate, startTime, timezone);
    const deadlineTimestamp = startTimestamp - advanceHours * 60 * 60 * 1000;

    // Convert back to Date object (UTC timestamp in ms)
    const deadlineDate = new Date(deadlineTimestamp);

    // Format time in the location's timezone
    const formatDeadlineTime = (date: Date, tz: string) => {
      const timeStr = date.toLocaleString('en-US', {
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
      return timeStr;
    };

    const formatDeadlineDate = (date: Date, tz: string) => {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    };

    return {
      date: deadlineDate,
      formattedDate: formatDeadlineDate(deadlineDate, timezone),
      formattedTime: formatDeadlineTime(deadlineDate, timezone),
      hours: advanceHours,
    };
  }, [startDate, startTime, car?.location?.timezone, car?.freeCancellationAdvanceHours]);

  // Debounce pricing inputs to prevent rapid firing
  const debouncedStartAt = useDebouncedValue(startTimestamp || 0, 400);
  const debouncedEndAt = useDebouncedValue(endTimestamp || 0, 400);
  const debouncedDriverAge = useDebouncedValue(driverAge, 400);

  const isValidRange =
    Boolean(debouncedStartAt) && Boolean(debouncedEndAt) && debouncedEndAt > debouncedStartAt;

  // Centralized pricing query
  const {
    data: pricingData,
    loading: pricingLoading,
    error: pricingErrorResult,
  } = useQuery(CALCULATE_CAR_PRICING_WITHOUT_TAX, {
    variables: {
      input: {
        carId: carId || '',
        startAt: debouncedStartAt,
        endAt: debouncedEndAt,
        driverAge: debouncedDriverAge,
        pickupLocationId: pickupLocationId ?? undefined,
      },
    },
    skip: !carId || !isValidRange || Boolean(dateConflictError || validationError),
    errorPolicy: 'all',
  });

  // Watch for pricing errors in the centralized query
  useEffect(() => {
    if (!isValidRange) {
      setPricingError(null);
      lastErrorKeyRef.current = null;
      return;
    }

    if (pricingErrorResult) {
      const msg =
        pricingErrorResult?.graphQLErrors?.[0]?.message ||
        pricingErrorResult?.message ||
        'Failed to calculate pricing';
      const errorKey = `${msg}:${debouncedStartAt}:${debouncedEndAt}:${debouncedDriverAge}`;

      if (lastErrorKeyRef.current !== errorKey) {
        lastErrorKeyRef.current = errorKey;
        handlePriceError(msg);
        toast.error(msg);
        triggerErrorEffects();
      }
    } else if (!pricingLoading && pricingData) {
      lastErrorKeyRef.current = null;
      setPricingError(null);
    }
  }, [
    pricingErrorResult,
    pricingLoading,
    pricingData,
    debouncedStartAt,
    debouncedEndAt,
    debouncedDriverAge,
    isValidRange,
    handlePriceError,
    triggerErrorEffects,
  ]);

  // We keep the error key stable until a successful calculation or a significant enough change
  // Success clears the key in the useEffect above.

  if (carLoading || userLoading) return <ViewCarPageSkeleton />;
  if (carError || userError)
    return <div>Error: 'Something went wrong please come back later and retry'</div>;

  const onReturnLocationChange = (e: any) => {
    e.preventDefault();
    setReturnLocationId(Number(e.target.value));
  };

  const getLocationById = (id: number, isReturn: boolean = false) => {
    // First check if it's in locationsData
    const locationInRes = locationsData?.locations?.find(location => location.id === id);
    if (locationInRes && locationInRes?.city && locationInRes?.address && locationInRes?.state) {
      return {
        id: locationInRes.id,
        name: locationInRes.name,
        city: locationInRes.city,
        address: locationInRes.address,
        state: locationInRes.state,
        zipCode: locationInRes.zipCode,
      };
    }

    // If not found, check if we've fetched it separately (for public locations like airports)
    const fetchedLocation = isReturn ? returnLocationData : pickupLocationData;
    if (fetchedLocation && fetchedLocation.id === id) {
      return {
        id: fetchedLocation.id,
        name: fetchedLocation.name,
        city: fetchedLocation.city,
        address: fetchedLocation.address,
        state: fetchedLocation.state,
        zipCode: fetchedLocation.zipCode,
      };
    }

    return null;
  };

  const isCarType = !carType || carType === ProductType.Car;

  // Format feature enum to readable text (same as mobile)
  const formatFeatureName = (feature: string): string => {
    const featureMap: Record<string, string> = {
      ALL_WHEEL_DRIVE: 'All-wheel drive',
      ANDROID_AUTO: 'Android Auto',
      APPLE_CARPLAY: 'Apple CarPlay',
      AUX_INPUT: 'Aux input',
      BACKUP_CAMERA: 'Backup camera',
      BIKE_RACK: 'Bike rack',
      BLIND_SPOT_WARNING: 'Blind spot warning',
      BLUETOOTH: 'Bluetooth',
      CHILD_SEAT: 'Child seat',
      CONVERTIBLE: 'Convertible',
      GPS: 'GPS',
      HEATED_SEATS: 'Heated seats',
      KEYLESS_ENTRY: 'Keyless entry',
      PET_FRIENDLY: 'Pet friendly',
      SKI_RACK: 'Ski rack',
      SNOW_TIRES: 'Snow tires',
      SUNROOF: 'Sunroof',
      TOLL_PASS: 'Toll pass',
      USB_CHARGER: 'USB charger',
      USB_INPUT: 'USB input',
      WHEELCHAIR_ACCESSIBLE: 'Wheelchair accessible',
    };
    return (
      featureMap[feature] ||
      feature
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase())
    );
  };

  const totalPhotos = imageUrls.length;
  // Host info from car data
  const hostName =
    car?.owner?.displayName ||
    `${car?.owner?.firstName || ''} ${car?.owner?.lastName || ''}`.trim() ||
    'Host';

  const calculatedPrice = pricingData?.calculateCarPricingWithoutTaxResolver?.calculatedPrice;
  const tripSavings = calculatedPrice?.discountAmount || 0;

  return (
    <PageContainer>
      <MobilePagePadding>
        <FlexContainer>
          {/* Mobile Layout: Image, Title/Rating, Calendar, then other content */}
          {/* Desktop Layout: Left column with all details, Right column with booking */}

          {/* Mobile: Single Image with View Photos Button (only on small screens) */}
          <MobileThumbnailWrapper>
            <MobileThumbnailContainer>
              <MobileThumbnailImage
                src={
                  imageUrls[0] || `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${carData?.car?.thumbnailUrl}`
                }
                alt={`${car?.make} ${car?.model}`}
                onClick={() => {
                  if (imageUrls.length > 0) {
                    setModalImageIndex(0);
                    setIsPhotoModalOpen(true);
                  }
                }}
                style={{ cursor: imageUrls.length > 0 ? 'pointer' : 'default' }}
              />
              <FavoriteButton
                $isFavorite={isFavorite}
                onClick={async e => {
                  e.stopPropagation();
                  if (car && auth.isAuthenticated) {
                    await toggleFavorite(car.id, isFavorite);
                  } else if (!auth.isAuthenticated) {
                    modalActions.openSignInModal();
                  }
                }}
              >
                <FontAwesomeIcon icon={isFavorite ? faHeartSolid : faHeartOutline} />
              </FavoriteButton>
              {totalPhotos > 1 && (
                <ViewPhotosButton
                  onClick={e => {
                    e.stopPropagation();
                    setModalImageIndex(0);
                    setIsPhotoModalOpen(true);
                  }}
                >
                  <FontAwesomeIcon icon={faCamera} />
                  View {totalPhotos} {totalPhotos === 1 ? 'photo' : 'photos'}
                </ViewPhotosButton>
              )}
            </MobileThumbnailContainer>
          </MobileThumbnailWrapper>

          {/* Mobile: Title and Rating (only on small screens) */}
          <MobileTitleRatingWrapper>
            <CarTitle>
              {isCarType
                ? `${car?.make ?? ''} ${car?.model ?? ''} ${car?.year ?? ''}`.trim()
                : car?.displayName ||
                  `${car?.make ?? ''} ${car?.model ?? ''} ${car?.year ?? ''}`.trim()}
            </CarTitle>
            <ModelType>Base</ModelType>
            <RatingSection>
              <FontAwesomeIcon icon={faStar} color="var(--color-accent)" />
              <RatingText>{car?.averageRating?.toFixed(1) || 'New'}</RatingText>
            </RatingSection>
          </MobileTitleRatingWrapper>

          {/* Mobile: Calendar Section (only on small screens) */}
          <MobileCalendarWrapper>
            <BookingSection style={{ position: 'static', width: '100%' }}>
              <TripSection>
                <TripLabel>Your trip</TripLabel>
                <div ref={mobileDateErrorRef}>
                  <DatePickerWrapper
                    $hasError={Boolean(dateConflictError || validationError)}
                    $shouldShake={shouldShake}
                  >
                    <DateRangeTrigger type="button" onClick={() => setDateModalOpen(true)}>
                      <div>
                        <DateRangeText>{formatDateRangeLabel(startDate, endDate)}</DateRangeText>
                        <DateRangeSubText>Select pickup and return dates</DateRangeSubText>
                      </div>
                    </DateRangeTrigger>
                    <TimeSelectRow>
                      <TimeSelect value={startTime} onChange={e => setStartTime(e.target.value)}>
                        {startAtOptions.map(time => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </TimeSelect>
                      <TimeSelect value={endTime} onChange={e => setEndTime(e.target.value)}>
                        {endAtOptions.map(time => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </TimeSelect>
                    </TimeSelectRow>
                  </DatePickerWrapper>
                </div>
                {dateConflictError && (
                  <div ref={mobileDateErrorRef}>
                    <DateErrorContainer $shouldShake={shouldShake}>
                      <DateErrorText>{dateConflictError}</DateErrorText>
                    </DateErrorContainer>
                  </div>
                )}
                {(validationError || pricingError) && !dateConflictError && (
                  <div ref={mobileDateErrorRef}>
                    <DateErrorContainer $shouldShake={shouldShake}>
                      <DateErrorText>{validationError || pricingError}</DateErrorText>
                    </DateErrorContainer>
                  </div>
                )}
              </TripSection>

              {/* Mobile: Pickup & Return Location (read-only, below dates) */}
              <TripLocation
                location={getLocationById(pickupLocationId || 0, false) || carData?.car?.location}
                isAirport={Boolean(
                  pickupLocationId !== null &&
                  pickupLocationId !== carData?.car?.location?.id &&
                  (pickupLocationData?.name || getLocationById(pickupLocationId || 0, false)?.name),
                )}
              />

              {/* Mobile: Age Selector */}
              <div style={{ marginTop: '24px' }}>
                <Label>Driver Age</Label>
                <StyledAgeSelect
                  value={driverAge}
                  onChange={e => setDriverAge(Number(e.target.value))}
                >
                  {ageOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </StyledAgeSelect>
              </div>
            </BookingSection>
          </MobileCalendarWrapper>

          <DetailsSection>
            {/* Desktop: Image Gallery (only on md+ screens) */}
            <DesktopImageGalleryContainer>
              <MainImageContainer>
                <MainImage
                  src={
                    imageUrls[selectedImageIndex] ||
                    `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${carData?.car?.thumbnailUrl}`
                  }
                  alt={`${car?.make} ${car?.model}`}
                />
                <FavoriteButton
                  $isFavorite={isFavorite}
                  onClick={async e => {
                    e.stopPropagation();
                    if (car && auth.isAuthenticated) {
                      await toggleFavorite(car.id, isFavorite);
                    } else if (!auth.isAuthenticated) {
                      modalActions.openSignInModal();
                    }
                  }}
                >
                  <FontAwesomeIcon icon={isFavorite ? faHeartSolid : faHeartOutline} />
                </FavoriteButton>
              </MainImageContainer>
              {imageUrls.length > 0 && (
                <ThumbnailContainer>
                  {imageUrls.slice(0, 3).map((imageUrl, index) => (
                    <ThumbnailImage
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      style={{
                        border: selectedImageIndex === index ? `3px solid ${Colors.Black}` : 'none',
                      }}
                    >
                      <ThumbnailImg
                        src={imageUrl}
                        alt={`${car?.make} ${car?.model} - Image ${index + 1}`}
                      />
                      {index === imageUrls.slice(0, 3).length - 1 && totalPhotos > 3 && (
                        <ViewPhotosButton
                          onClick={e => {
                            e.stopPropagation();
                            setModalImageIndex(0);
                            setIsPhotoModalOpen(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faCamera} />
                          View {totalPhotos} {totalPhotos === 1 ? 'photo' : 'photos'}
                        </ViewPhotosButton>
                      )}
                    </ThumbnailImage>
                  ))}
                </ThumbnailContainer>
              )}
            </DesktopImageGalleryContainer>

            {/* Desktop: Vehicle Title (only on md+ screens) */}
            <DesktopTitleRatingWrapper>
              <CarTitle>
                {isCarType
                  ? `${car?.make ?? ''} ${car?.model ?? ''} ${car?.year ?? ''}`.trim()
                  : car?.displayName ||
                    `${car?.make ?? ''} ${car?.model ?? ''} ${car?.year ?? ''}`.trim()}
              </CarTitle>

              {/* Rating */}
              <RatingSection>
                <FontAwesomeIcon icon={faStar} color="var(--color-accent)" />
                <RatingText>{car?.averageRating?.toFixed(1) || 'New'}</RatingText>
              </RatingSection>
            </DesktopTitleRatingWrapper>

            {/* Vehicle Specs */}
            {!isCarType ? (
              <CarSpecs>
                <SpecItem>
                  <span>Make: {car?.make ?? 'N/A'}</span>
                </SpecItem>
                <SpecItem>
                  <span>Model: {car?.model ?? 'N/A'}</span>
                </SpecItem>
                <SpecItem>
                  <span>Year: {car?.year ?? 'N/A'}</span>
                </SpecItem>
              </CarSpecs>
            ) : (
              <CarSpecs>
                <SpecItem>
                  <FontAwesomeIcon icon={faUser} />
                  <span>{car?.seats || 2} seats</span>
                </SpecItem>
                <SpecItem>
                  <FontAwesomeIcon icon={faGasPump} />
                  <span>{car?.gasGrade || car?.gas || 'Gas'}</span>
                </SpecItem>
                <SpecItem>
                  <FontAwesomeIcon icon={faCog} />
                  <span>{car?.gearType?.toLowerCase() || 'automatic'} transmission</span>
                </SpecItem>
              </CarSpecs>
            )}

            {/* Mobile: Policy sections (above Hosted by in column view) */}
            <MobileOnly>
              <PolicySection>
                <PolicyItem>
                  <PolicyIcon>
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </PolicyIcon>
                  <PolicyContent>
                    <PolicyTitle>Free cancellation</PolicyTitle>
                    <PolicyDescription>
                      {cancellationDeadline ? (
                        <>
                          Full refund before {cancellationDeadline.formattedDate},{' '}
                          {cancellationDeadline.formattedTime}
                        </>
                      ) : (
                        <>Full refund before trip start</>
                      )}
                    </PolicyDescription>
                  </PolicyContent>
                </PolicyItem>
              </PolicySection>

              <PolicySection>
                <PolicyItem>
                  <PolicyIcon>
                    <FontAwesomeIcon icon={faCreditCard} />
                  </PolicyIcon>
                  <PolicyContent>
                    <PolicyTitle>Flexible payment</PolicyTitle>
                    <PolicyDescription>
                      $0 due now when you choose the Refundable option at checkout.
                    </PolicyDescription>
                  </PolicyContent>
                </PolicyItem>
              </PolicySection>

              {car?.distanceLimit && (
                <PolicySection>
                  <PolicyItem>
                    <PolicyIcon>
                      <FontAwesomeIcon icon={faTachometerAlt} />
                    </PolicyIcon>
                    <PolicyContent>
                      <PolicyTitle>Distance included</PolicyTitle>
                      <PolicyDescription>
                        {car.distanceLimit.unlimitedMileage ? (
                          <>Unlimited</>
                        ) : (
                          <>
                            {(() => {
                              const totalDays =
                                calculatedPrice?.totalDays ||
                                (startDate && endDate
                                  ? Math.ceil(
                                      (endDate.getTime() - startDate.getTime()) /
                                        (1000 * 60 * 60 * 24),
                                    )
                                  : 1);
                              const totalMiles =
                                (car.distanceLimit.dailyDistanceLimit || 0) * totalDays;
                              return distanceUnit === 'km'
                                ? `${totalMiles} km in total`
                                : `${totalMiles} total miles`;
                            })()}
                            {car.distanceLimit.dailyDistanceLimit && (
                              <>
                                {' '}
                                • {car.distanceLimit.dailyDistanceLimit}{' '}
                                {distanceUnit === 'km' ? 'km' : 'miles'} per day
                              </>
                            )}
                          </>
                        )}
                      </PolicyDescription>
                    </PolicyContent>
                  </PolicyItem>
                </PolicySection>
              )}
            </MobileOnly>

            {/* Host Section */}
            {car?.owner && (
              <HostSection>
                <SectionLabel>Hosted by</SectionLabel>
                <HostInfo>
                  <HostImage
                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(hostName)}&background=random`}
                    alt={hostName}
                  />
                  <HostDetails>
                    <HostName>{hostName}</HostName>
                    <HostRating>
                      <HostRatingBadge>
                        <FontAwesomeIcon icon={faStar} size="xs" /> 5.0
                      </HostRatingBadge>
                    </HostRating>
                  </HostDetails>
                </HostInfo>
              </HostSection>
            )}

            {/* Features Section */}
            {car?.features && car.features.length > 0 && (
              <FeaturesSection>
                <SectionLabel>Vehicle features</SectionLabel>
                <FeatureList>
                  {car.features.map((feature: string, index: number) => (
                    <FeatureItem key={index}>
                      <FontAwesomeIcon icon={faCheckCircle} size="sm" color={Colors.Black} />
                      <span>{formatFeatureName(feature)}</span>
                    </FeatureItem>
                  ))}
                </FeatureList>
              </FeaturesSection>
            )}

            {/* Included in Price */}
            <IncludedSection>
              <SectionLabel>Included in the price</SectionLabel>

              <IncludedCategory>
                <CategoryTitle>Convenience</CategoryTitle>
                <IncludedItem>
                  <IncludedIcon>
                    <SedanIcon size={24} />
                  </IncludedIcon>
                  <IncludedContent>
                    <IncludedTitle>Skip the rental counter</IncludedTitle>
                    <IncludedDescription>
                      Use the app for pickup and return instructions
                    </IncludedDescription>
                  </IncludedContent>
                </IncludedItem>
                <IncludedItem>
                  <IncludedIcon>
                    <FontAwesomeIcon icon={faUser} />
                  </IncludedIcon>
                  <IncludedContent>
                    <IncludedTitle>Add additional drivers for free</IncludedTitle>
                  </IncludedContent>
                </IncludedItem>
                <IncludedItem>
                  <IncludedIcon>
                    <FontAwesomeIcon icon={faClock} />
                  </IncludedIcon>
                  <IncludedContent>
                    <IncludedTitle>30-minute return grace period</IncludedTitle>
                    <IncludedDescription>
                      No need to extend your trip unless you're running more than 30 minutes late
                    </IncludedDescription>
                  </IncludedContent>
                </IncludedItem>
              </IncludedCategory>

              <IncludedCategory>
                <CategoryTitle>Peace of mind</CategoryTitle>
                <IncludedItem>
                  <IncludedIcon>
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </IncludedIcon>
                  <IncludedContent>
                    <IncludedDescription>
                      No need to wash the car before returning it, but please keep the vehicle tidy.
                    </IncludedDescription>
                  </IncludedContent>
                </IncludedItem>
                <IncludedItem>
                  <IncludedIcon>
                    <FontAwesomeIcon icon={faShieldAlt} />
                  </IncludedIcon>
                  <IncludedContent>
                    <IncludedTitle>Access to basic roadside assistance</IncludedTitle>
                  </IncludedContent>
                </IncludedItem>
                {/* <IncludedItem>
                  <IncludedIcon>
                    <FontAwesomeIcon icon={faHeadset} />
                  </IncludedIcon>
                  <IncludedContent>
                    <IncludedTitle>24/7 customer support</IncludedTitle>
                  </IncludedContent>
                </IncludedItem> */}
              </IncludedCategory>
            </IncludedSection>

            {/* Description */}
            <Description>
              <SectionLabel>DESCRIPTION</SectionLabel>
              <DescriptionText>
                {carData?.car?.description || 'No description available'}
              </DescriptionText>
            </Description>

            {carData?.car?.guestInstructions && (
              <Description>
                <SectionLabel>GUEST INSTRUCTIONS</SectionLabel>
                <DescriptionText>{carData.car.guestInstructions}</DescriptionText>
              </Description>
            )}

            {/* Ratings and Reviews */}
            {carId && <CarRatingsSection carId={carId} />}
          </DetailsSection>

          <BookingSection>
            {/* Pricing - Desktop only (mobile shows in fixed bar at bottom) */}
            <DesktopPriceSection>
              {carId && startTimestamp && endTimestamp && (
                <PriceInfoComponent
                  carId={carId}
                  data={pricingData}
                  loading={pricingLoading}
                  error={pricingErrorResult}
                  disablePricing={Boolean(validationError || dateConflictError)}
                  externalError={validationError || dateConflictError || pricingError}
                />
              )}
            </DesktopPriceSection>

            {/* Trip Dates - Desktop only (mobile shows above) */}
            <DesktopTripSection>
              <TripLabel>Your trip</TripLabel>
              <div ref={dateErrorRef}>
                <DatePickerWrapper
                  $hasError={Boolean(dateConflictError || validationError)}
                  $shouldShake={shouldShake}
                >
                  <DateRangeTrigger type="button" onClick={() => setDateModalOpen(true)}>
                    <div>
                      <DateRangeText>{formatDateRangeLabel(startDate, endDate)}</DateRangeText>
                      <DateRangeSubText>Select pickup and return dates</DateRangeSubText>
                    </div>
                  </DateRangeTrigger>
                  <TimeSelectRow>
                    <TimeSelect value={startTime} onChange={e => setStartTime(e.target.value)}>
                      {startAtOptions.map(time => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </TimeSelect>
                    <TimeSelect value={endTime} onChange={e => setEndTime(e.target.value)}>
                      {endAtOptions.map(time => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </TimeSelect>
                  </TimeSelectRow>
                </DatePickerWrapper>
              </div>
              {dateConflictError && (
                <div>
                  <DateErrorContainer $shouldShake={shouldShake}>
                    <DateErrorText>{dateConflictError}</DateErrorText>
                  </DateErrorContainer>
                </div>
              )}
              {(validationError || pricingError) && !dateConflictError && (
                <div>
                  <DateErrorContainer $shouldShake={shouldShake}>
                    <DateErrorText>{validationError || pricingError}</DateErrorText>
                  </DateErrorContainer>
                </div>
              )}
            </DesktopTripSection>

            {/* Pickup & Return Location - Desktop only (read-only, not selectable) */}
            <TripLocation
              location={getLocationById(pickupLocationId || 0, false) || carData?.car?.location}
              isDesktopOnly
              isAirport={
                pickupLocationId !== null &&
                pickupLocationId !== carData?.car?.location?.id &&
                Boolean(
                  pickupLocationData?.name || getLocationById(pickupLocationId || 0, false)?.name,
                )
              }
            />

            {/* Trip Savings */}
            {pricingData?.calculateCarPricingWithoutTaxResolver?.calculatedPrice &&
            pricingData.calculateCarPricingWithoutTaxResolver.calculatedPrice.discountAmount &&
            pricingData.calculateCarPricingWithoutTaxResolver.calculatedPrice.discountAmount > 0 &&
            calculatedPrice?.discountTier ? (
              <TripSavingsSection>
                <SavingsItem>
                  <SavingsLabel>{calculatedPrice?.discountTier}</SavingsLabel>
                  <SavingsAmount>${calculatedPrice?.discountAmount}</SavingsAmount>
                </SavingsItem>
              </TripSavingsSection>
            ) : null}

            {/* Driver Age Selector - Desktop only */}
            <DesktopAgeSelectContainer>
              <Label>Driver Age</Label>
              <StyledAgeSelect
                value={driverAge}
                onChange={e => setDriverAge(Number(e.target.value))}
              >
                {ageOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </StyledAgeSelect>
            </DesktopAgeSelectContainer>

            {/* Continue Button - Desktop only (mobile shows in fixed bar at bottom) */}
            <DesktopContinueButtonWrapper>
              <ContinueButton
                onClick={() => void handleContinueBooking()}
                disabled={makeBookingLoading || redirectInProgress || Boolean(notBookableError)}
              >
                {redirectInProgress
                  ? 'Redirecting...'
                  : makeBookingLoading
                    ? 'Loading...'
                    : 'Continue'}
              </ContinueButton>
              {notBookableError ? (
                <AirportNotBookableNotice message={notBookableError} />
              ) : ownerRedirectTarget ? (
                <OwnerSiteRedirectNotice ownerSiteHost={ownerRedirectTarget.ownerSiteHost} />
              ) : null}
            </DesktopContinueButtonWrapper>

            {/* Cancellation / Payment / Distance - Desktop only (mobile shows above Hosted by) */}
            <DesktopOnly>
              <PolicySection>
                <PolicyItem>
                  <PolicyIcon>
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </PolicyIcon>
                  <PolicyContent>
                    <PolicyTitle>Free cancellation</PolicyTitle>
                    <PolicyDescription>
                      {cancellationDeadline ? (
                        <>
                          Full refund before {cancellationDeadline.formattedDate},{' '}
                          {cancellationDeadline.formattedTime}
                        </>
                      ) : (
                        <>Full refund before trip start</>
                      )}
                    </PolicyDescription>
                  </PolicyContent>
                </PolicyItem>
              </PolicySection>

              <PaymentOptionsSection>
                <PolicyItem>
                  <PolicyIcon>
                    <FontAwesomeIcon icon={faCreditCard} />
                  </PolicyIcon>
                  <PolicyContent>
                    <PolicyTitle>Flexible payment</PolicyTitle>
                    <PolicyDescription>
                      $0 due now when you choose the Refundable option at checkout.
                    </PolicyDescription>
                  </PolicyContent>
                </PolicyItem>
              </PaymentOptionsSection>

              {car?.distanceLimit && (
                <DistanceSection>
                  <PolicyItem>
                    <PolicyIcon>
                      <FontAwesomeIcon icon={faTachometerAlt} />
                    </PolicyIcon>
                    <PolicyContent>
                      <PolicyTitle>Distance included</PolicyTitle>
                      <PolicyDescription>
                        {car.distanceLimit.unlimitedMileage ? (
                          <>Unlimited</>
                        ) : (
                          <>
                            {(() => {
                              const totalDays =
                                calculatedPrice?.totalDays ||
                                (startDate && endDate
                                  ? Math.ceil(
                                      (endDate.getTime() - startDate.getTime()) /
                                        (1000 * 60 * 60 * 24),
                                    )
                                  : 1);
                              const totalMiles =
                                (car.distanceLimit.dailyDistanceLimit || 0) * totalDays;
                              return distanceUnit === 'km'
                                ? `${totalMiles} km in total`
                                : `${totalMiles} total miles`;
                            })()}
                            {car.distanceLimit.dailyDistanceLimit && (
                              <>
                                {' '}
                                • {car.distanceLimit.dailyDistanceLimit}{' '}
                                {distanceUnit === 'km' ? 'km' : 'miles'} per day
                              </>
                            )}
                          </>
                        )}
                      </PolicyDescription>
                    </PolicyContent>
                  </PolicyItem>
                </DistanceSection>
              )}
            </DesktopOnly>
          </BookingSection>
        </FlexContainer>
      </MobilePagePadding>

      {/* Mobile Fixed Price Bar at Bottom */}
      <MobileFixedPriceBar>
        {carId && startTimestamp && endTimestamp ? (
          <MobilePriceInfoComponent
            carId={carId}
            data={pricingData}
            loading={pricingLoading}
            error={pricingErrorResult}
            disablePricing={Boolean(validationError || dateConflictError)}
            externalError={validationError || dateConflictError || pricingError || notBookableError}
            onContinue={() => void handleContinueBooking()}
            isLoading={makeBookingLoading || redirectInProgress}
            ownerSiteRedirectHost={ownerRedirectTarget?.ownerSiteHost ?? null}
          />
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
            Select dates to see price
          </div>
        )}
      </MobileFixedPriceBar>

      <DateRangeModal
        isOpen={isDateModalOpen}
        onClose={() => setDateModalOpen(false)}
        startDate={startDate}
        endDate={endDate}
        onSave={(start, end, pick) => {
          const normalizedStart = start <= end ? start : end;
          const normalizedEnd = start <= end ? end : start;
          const minimumDays = Math.max((carData?.car?.minimumRentalDays || 1) - 1, 0);
          const minEndDate = new Date(normalizedStart);
          minEndDate.setDate(minEndDate.getDate() + minimumDays);
          const adjustedEnd = normalizedEnd < minEndDate ? minEndDate : normalizedEnd;

          setStartDate(normalizedStart);
          setEndDate(adjustedEnd);
          // Spec 028: a monthly trip keeps the return time on the pickup time (whole days).
          setTripPick(pick);
          if (pick.mode === 'months') setEndTime(startTime);
          setDateModalOpen(false);
        }}
        allowMonths={monthlyAllowed}
        initialMode={tripPick.mode}
        initialMonths={tripPick.months}
        minDate={minSelectableDate}
        timezone={listingZone}
        blockedDays={tripDaySets.blocked}
        partialDays={tripDaySets.partial}
        validateRange={validateTripRange}
        startTimeLabel={startTime}
        endTimeLabel={endTime}
      />

      {/* Photo Gallery Modal */}
      {isPhotoModalOpen && imageUrls.length > 0 && (
        <PhotoModalOverlay>
          <PhotoModalContent>
            <PhotoModalHeader>
              <PhotoModalCloseButton onClick={() => setIsPhotoModalOpen(false)}>
                <FontAwesomeIcon icon={faClose} />
              </PhotoModalCloseButton>
            </PhotoModalHeader>
            <PhotoModalBody>
              <PhotoGrid>
                {imageUrls.map((imageUrl, index) => (
                  <PhotoGridItem key={index}>
                    <PhotoGridImg
                      src={imageUrl}
                      alt={`${car?.make} ${car?.model} - Image ${index + 1}`}
                    />
                  </PhotoGridItem>
                ))}
              </PhotoGrid>
            </PhotoModalBody>
          </PhotoModalContent>
        </PhotoModalOverlay>
      )}

      <ReferralOwnerTermsModal
        isOpen={showOwnerTermsModal}
        ownerSiteHost={formatOwnerSiteDisplayHost(getCurrentSiteHost())}
        onContinue={() => {
          void handleOwnerTermsContinue();
        }}
        isLoading={handoffCheckoutInProgress || makeBookingLoading}
      />
    </PageContainer>
  );
};

export default GuestViewCarPage;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debouncedValue;
}

const PriceInfo = styled.div`
  ${tw`
    w-full
    text-lg
    mb-4
  `}
`;

const PriceInfoLoading = styled(PriceInfo)`
  ${tw`
    flex flex-row gap-2
  `}
`;

const BeforeTaxesPrice = styled.span`
  ${tw`
    text-red-500
    line-through
    text-sm
    `}
`;

export const PriceInfoComponent = ({
  carId,
  data,
  loading,
  error,
  disablePricing = false,
  externalError,
}: {
  carId: string;
  data: any;
  loading: boolean;
  error: any;
  disablePricing?: boolean;
  externalError?: string | null;
}) => {
  if (loading)
    return (
      <PriceInfoLoading>
        <span>total:</span>
        <MoonLoader color="teal" size={24} />
      </PriceInfoLoading>
    );

  const displayErrorMessage = externalError || error?.graphQLErrors?.[0]?.message || error?.message;

  const calculatedPrice = data?.calculateCarPricingWithoutTaxResolver?.calculatedPrice;
  const totalPrice = calculatedPrice?.totalPrice;

  if (disablePricing || error || !totalPrice) {
    return (
      <PriceInfo>
        <div>
          <span>total:</span> <span style={{ color: Colors.Error }}>Unable to calculate</span>
        </div>
        {displayErrorMessage && (
          <div
            style={{ color: Colors.Error, fontSize: '12px', marginTop: '4px', maxWidth: '300px' }}
          >
            {displayErrorMessage}
          </div>
        )}
      </PriceInfo>
    );
  }

  const discountAmount = calculatedPrice?.discountAmount;
  const beforeDiscount = formatNumber((totalPrice ?? 0) + (discountAmount ?? 0));
  return (
    <div>
      {discountAmount ? (
        <PriceAmount>
          <PriceOriginal>${beforeDiscount}</PriceOriginal>${totalPrice} total
        </PriceAmount>
      ) : (
        <PriceAmount>${totalPrice} total</PriceAmount>
      )}
      <PriceBeforeTaxes>Before taxes and fees</PriceBeforeTaxes>
    </div>
  );
};

// Mobile Price Component for fixed bottom bar
export const MobilePriceInfoComponent = ({
  carId,
  data,
  loading,
  error,
  disablePricing = false,
  externalError,
  onContinue,
  isLoading = false,
  ownerSiteRedirectHost = null,
}: {
  carId: string;
  data: any;
  loading: boolean;
  error: any;
  disablePricing?: boolean;
  externalError?: string | null;
  onContinue: () => void;
  isLoading?: boolean;
  ownerSiteRedirectHost?: string | null;
}) => {
  if (loading) {
    return (
      <MobilePriceContent>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MoonLoader color={Colors.Purple600} size={20} />
          <span style={{ color: Colors.Gray400 }}>Calculating...</span>
        </div>
      </MobilePriceContent>
    );
  }

  const calculatedPrice = data?.calculateCarPricingWithoutTaxResolver?.calculatedPrice;
  const totalPrice = calculatedPrice?.totalPrice;
  const displayErrorMessage = externalError || error?.graphQLErrors?.[0]?.message || error?.message;
  const continueLabel = isLoading ? 'Loading...' : 'Continue';

  if (disablePricing || error || !totalPrice) {
    return (
      <div>
        <MobilePriceContent>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, paddingRight: '8px' }}>
            <div style={{ color: Colors.Error }}>Unable to calculate</div>
            {displayErrorMessage && (
              <div
                style={{
                  color: Colors.Error,
                  fontSize: '10px',
                  marginTop: '2px',
                  lineHeight: '1.2',
                }}
              >
                {displayErrorMessage}
              </div>
            )}
          </div>
          <MobileContinueButton onClick={onContinue} disabled={isLoading}>
            {continueLabel}
          </MobileContinueButton>
        </MobilePriceContent>
        {ownerSiteRedirectHost ? (
          <OwnerSiteRedirectNotice ownerSiteHost={ownerSiteRedirectHost} />
        ) : null}
      </div>
    );
  }

  const discountAmount = calculatedPrice?.discountAmount;
  const beforeDiscount = formatNumber((totalPrice ?? 0) + (discountAmount ?? 0));

  return (
    <div>
      <MobilePriceContent>
        <div>
          {discountAmount ? (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <span
                style={{ textDecoration: 'line-through', color: Colors.Gray400, fontSize: '16px' }}
              >
                ${beforeDiscount}
              </span>
              <MobilePriceAmount>${totalPrice} total</MobilePriceAmount>
            </div>
          ) : (
            <MobilePriceAmount>${totalPrice} total</MobilePriceAmount>
          )}
          <MobilePriceBeforeTaxes>Before taxes and fees</MobilePriceBeforeTaxes>
        </div>
        <MobileContinueButton onClick={onContinue} disabled={isLoading}>
          {continueLabel}
        </MobileContinueButton>
      </MobilePriceContent>
      {ownerSiteRedirectHost ? (
        <OwnerSiteRedirectNotice ownerSiteHost={ownerSiteRedirectHost} />
      ) : null}
    </div>
  );
};
